import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { getDateInputMask, getLocalizedDateInputFormat } from '../../utils/dateTime';
import { StudiesDashboardFiltersFields } from './useStudyFiltersForm';

const StudyFilters: React.FunctionComponent<
  React.PropsWithChildren<{
    formMethods: UseFormReturn<StudiesDashboardFiltersFields, any>;
  }>
> = ({ formMethods }) => {
  const { startDate, endDate, text } = formMethods.watch();
  const dateInputFormat = React.useMemo(() => getLocalizedDateInputFormat(), []);
  const dateInputMask = React.useMemo(() => getDateInputMask(dateInputFormat), [dateInputFormat]);

  const normalizeDateValue = (nextValue: Dayjs | Date | null): Date | null => {
    if (!nextValue) {
      return null;
    }

    return 'toDate' in nextValue ? nextValue.toDate() : nextValue;
  };

  const setDateValue = (fieldName: 'startDate' | 'endDate', nextValue: Dayjs | Date | null) => {
    const normalizedDate = normalizeDateValue(nextValue);

    formMethods.setValue(fieldName, normalizedDate, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const setStartDate = (date: Dayjs | Date | null) => setDateValue('startDate', date);

  const setEndDate = (date: Dayjs | Date | null) => setDateValue('endDate', date);

  const setText = (value: string) => {
    formMethods.setValue('text', value);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Preserve the native search-field expectation that Escape clears the current query.
    if (event.key === 'Escape' && Boolean(text)) {
      event.preventDefault();
      setText('');
    }
  };

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value);
  };

  const onReset = () => {
    setText('');
  };

  const getDateInputProps = (
    params: Record<string, any>,
    value: Date | null,
    startAdornmentLabel: string,
    clearAriaLabel: string,
    onClear: () => void
  ) => ({
    ...params.InputProps,
    startAdornment: (
      <InputAdornment position="start">
        <Typography variant="body2" color="secondary">
          {startAdornmentLabel}
        </Typography>
      </InputAdornment>
    ),
    endAdornment: (
      <>
        {value ? (
          <InputAdornment position="end">
            <IconButton aria-label={clearAriaLabel} onClick={onClear} edge="end" size="small" sx={{ mr: 0.25 }}>
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null}
        {params.InputProps?.endAdornment}
      </>
    ),
  });

  return (
    <Grid item container alignItems="center" spacing={2} wrap="nowrap">
      <Grid item xs sx={{ minWidth: 280 }}>
        <TextField
          fullWidth
          type="search"
          sx={{
            '& input[type="search"]::-webkit-search-cancel-button': {
              WebkitAppearance: 'none',
              appearance: 'none',
              display: 'none',
            },
            '& input[type="search"]::-webkit-search-decoration': {
              WebkitAppearance: 'none',
              appearance: 'none',
            },
          }}
          label="Search Studies"
          placeholder="Search by study name, description, or ID"
          value={text}
          onChange={onTextChange}
          autoComplete="off"
          data-test-class="input-text-filter"
          onKeyDown={onKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon color="secondary" />
              </InputAdornment>
            ),
            ...(text
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="Clear study search" onClick={onReset} edge="end" sx={{ mx: 0 }}>
                        <CloseOutlinedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              : {}),
          }}
        />
      </Grid>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid item sx={{ flexShrink: 0, width: { xs: 220, sm: 250 } }}>
          <DesktopDatePicker
            label="Created from"
            inputFormat={dateInputFormat}
            value={startDate}
            onChange={setStartDate}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                InputProps={{
                  ...getDateInputProps(params, startDate, 'From', 'Clear created from date', () => setStartDate(null)),
                }}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Created from',
                }}
                error={params.error || (formMethods.formState.isDirty && !formMethods.formState.isValid)}
              />
            )}
            className="date-picker-start"
            mask={dateInputMask}
          />
        </Grid>

        <Grid item sx={{ flexShrink: 0, width: { xs: 220, sm: 250 } }}>
          <DesktopDatePicker
            label="Created before"
            inputFormat={dateInputFormat}
            value={endDate}
            onChange={setEndDate}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                InputProps={{
                  ...getDateInputProps(params, endDate, 'To', 'Clear created before date', () => setEndDate(null)),
                }}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Created before',
                }}
                error={params.error || (formMethods.formState.isDirty && !formMethods.formState.isValid)}
              />
            )}
            className="date-picker-end"
            mask={dateInputMask}
          />
        </Grid>
      </LocalizationProvider>
    </Grid>
  );
};

export default StudyFilters;
